/**
 * Avaliações (CSAT): cards de resumo, distribuição de notas 1–5 em barras
 * horizontais e tabela das respostas recentes.
 */
import type { ReactNode } from 'react';
import { StarIcon } from 'lucide-react';
import type { RatingsReport } from '@erp/shared';
import { formatDateTime } from '@/lib/utils';
import { fmtDecimal, fmtPct, TD_NUM, TH, TH_NUM, TicketLink } from './report-format';
import type { CsvTable } from './report-views';

export function RatingsView({ data }: { data: RatingsReport }) {
  return (
    <div className="space-y-4 p-4">
      {/* Resumo */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard label="Pesquisas enviadas" value={String(data.sent)} />
        <SummaryCard label="Respostas" value={String(data.responses)} />
        <SummaryCard label="Taxa de resposta" value={fmtPct(data.responseRatePct)} />
        <SummaryCard
          label="Nota média"
          value={fmtDecimal(data.average)}
          icon={
            <StarIcon
              className="size-5 fill-[#FAAD14] text-[#FAAD14]"
              strokeWidth={1.5}
              aria-hidden
            />
          }
        />
      </div>

      {/* Distribuição 1–5 */}
      <div className="rounded-lg border border-border/70 p-4">
        <h3 className="mb-3 text-[13px] font-semibold">Distribuição das notas</h3>
        <div className="space-y-2">
          {[5, 4, 3, 2, 1].map((score) => {
            const count = data.distribution[score - 1] ?? 0;
            const maxCount = Math.max(1, ...data.distribution);
            return (
              <div key={score} className="flex items-center gap-3 text-[13px]">
                <span className="flex w-8 shrink-0 items-center justify-end gap-0.5 font-medium tabular-nums">
                  {score}
                  <StarIcon className="size-3 fill-[#FAAD14] text-[#FAAD14]" aria-hidden />
                </span>
                <div className="h-4 flex-1 overflow-hidden rounded-sm bg-muted">
                  <div
                    className="h-full rounded-sm bg-primary transition-[width]"
                    style={{ width: `${(count / maxCount) * 100}%` }}
                  />
                </div>
                <span className="w-10 shrink-0 text-right text-muted-foreground tabular-nums">
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Respostas recentes */}
      <div className="overflow-hidden rounded-lg border border-border/70">
        <h3 className="px-4 py-3 text-[13px] font-semibold">Respostas recentes</h3>
        {data.recent.length === 0 ? (
          <p className="border-t border-divider px-4 py-6 text-center text-sm text-muted-foreground">
            Nenhuma resposta no período
          </p>
        ) : (
          <div className="overflow-x-auto border-t border-divider">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className={TH_NUM}>Nota</th>
                  <th className={TH}>Comentário</th>
                  <th className={TH}>Ticket</th>
                  <th className={TH}>Respondida em</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r, i) => (
                  <tr
                    key={i}
                    className="h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className={TD_NUM}>
                      <span className="inline-flex items-center gap-1 font-medium">
                        {r.score}
                        <StarIcon
                          className="size-3.5 fill-[#FAAD14] text-[#FAAD14]"
                          aria-hidden
                        />
                      </span>
                    </td>
                    <td className="px-4">
                      <span
                        className="block max-w-96 truncate text-muted-foreground"
                        title={r.comment ?? undefined}
                      >
                        {r.comment ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 whitespace-nowrap">
                      <TicketLink number={r.ticketNumber} />
                      <span
                        className="ml-2 inline-block max-w-56 truncate align-bottom text-muted-foreground"
                        title={r.ticketTitle}
                      >
                        {r.ticketTitle}
                      </span>
                    </td>
                    <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                      {formatDateTime(r.respondedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border/70 p-4">
      <p className="text-[13px] text-muted-foreground">{label}</p>
      <p className="mt-1 flex items-center gap-1.5 text-2xl font-semibold tabular-nums">
        {value}
        {icon}
      </p>
    </div>
  );
}

export function ratingsCsv(data: RatingsReport): CsvTable {
  return {
    headers: ['Nota', 'Comentário', 'Ticket', 'Título', 'Respondida em'],
    rows: data.recent.map((r) => [
      r.score,
      r.comment,
      r.ticketNumber,
      r.ticketTitle,
      formatDateTime(r.respondedAt),
    ]),
  };
}
